/* ============================================================
   Trasse Families — Hours.

   A school imposes a timetable; a household decides one and changes it. That
   difference is the whole screen: every value here is editable, including the
   times, which the phone deliberately cannot change (the mobile Hours screen
   toggles days only).

   Two things live here, and the second is the one that stops notifications
   being trained away:

     the week    — which days run, and at what time, per day.
     the breaks  — half-term, holidays, travel. Days nothing is expected.

   The cron already honours both. `dayPlanRules.onBreak` is consulted by
   duePrompts AND by windowOpen, so a household on holiday is not merely
   silenced — it never even costs a day-plan read. This screen is the only way
   to put a date in that list.

   ⚠ Writes go straight to Firestore, not through the backend: familySchedules
   rules already allow the household's fleet_admin to update its own row, and
   pin orgCode so the document cannot be walked out of the household.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FAM, DISPLAY_FONT, FAMILY_PAGE_WRAP } from './familyTheme';

/* Order matters: the week reads Mon-first, and weekends sit at the bottom
   where their "off by default" note belongs. */
const DAYS: { key: string; label: string; weekend: boolean }[] = [
  { key: 'mon', label: 'Monday', weekend: false },
  { key: 'tue', label: 'Tuesday', weekend: false },
  { key: 'wed', label: 'Wednesday', weekend: false },
  { key: 'thu', label: 'Thursday', weekend: false },
  { key: 'fri', label: 'Friday', weekend: false },
  { key: 'sat', label: 'Saturday', weekend: true },
  { key: 'sun', label: 'Sunday', weekend: true },
];

const HHMM_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

interface Leg { enabled: boolean; pickupAt: string }
interface DayCfg { morning: Leg; evening: Leg }
type Weekly = Record<string, DayCfg>;

export interface FamilyBreak { from: string; to: string; label: string }

const DEFAULT_MORNING = '07:10';
const DEFAULT_EVENING = '16:00';

function emptyWeekly(): Weekly {
  const w: Weekly = {};
  for (const d of DAYS) {
    w[d.key] = {
      morning: { enabled: !d.weekend, pickupAt: DEFAULT_MORNING },
      evening: { enabled: !d.weekend, pickupAt: DEFAULT_EVENING },
    };
  }
  return w;
}

/** Read the stored doc into the shape this screen edits, filling gaps rather
 *  than failing — a household created before a field existed must still open. */
function toWeekly(raw: unknown): Weekly {
  const src = (raw || {}) as Record<string, unknown>;
  const w: Weekly = {};
  for (const d of DAYS) {
    const day = (src[d.key] || {}) as Record<string, unknown>;
    const m = (day.morning || {}) as Record<string, unknown>;
    const e = (day.evening || {}) as Record<string, unknown>;
    w[d.key] = {
      morning: {
        enabled: m.enabled === true,
        pickupAt: HHMM_RE.test(String(m.pickupAt || '')) ? String(m.pickupAt) : DEFAULT_MORNING,
      },
      evening: {
        enabled: e.enabled === true,
        pickupAt: HHMM_RE.test(String(e.pickupAt || '')) ? String(e.pickupAt) : DEFAULT_EVENING,
      },
    };
  }
  return w;
}

function toBreaks(raw: unknown): FamilyBreak[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b) => {
      const o = (b || {}) as Record<string, unknown>;
      return {
        from: String(o.from || ''),
        to: String(o.to || ''),
        label: String(o.label || ''),
      };
    })
    .filter((b) => DATE_RE.test(b.from) && DATE_RE.test(b.to))
    .sort((a, b) => a.from.localeCompare(b.from));
}

function sameWeekly(a: Weekly, b: Weekly): boolean {
  return DAYS.every((d) => {
    const x = a[d.key]; const y = b[d.key];
    return x.morning.enabled === y.morning.enabled
      && x.morning.pickupAt === y.morning.pickupAt
      && x.evening.enabled === y.evening.enabled
      && x.evening.pickupAt === y.evening.pickupAt;
  });
}

function sameBreaks(a: FamilyBreak[], b: FamilyBreak[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => x.from === b[i].from && x.to === b[i].to && x.label === b[i].label);
}

function fmtRange(b: FamilyBreak): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const from = new Date(`${b.from}T00:00:00`);
  const to = new Date(`${b.to}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return `${b.from} — ${b.to}`;
  const year = to.getFullYear() === new Date().getFullYear() ? '' : ` ${to.getFullYear()}`;
  if (b.from === b.to) return `${from.toLocaleDateString('en-KE', opts)}${year}`;
  return `${from.toLocaleDateString('en-KE', opts)} — ${to.toLocaleDateString('en-KE', opts)}${year}`;
}

/** Today in the household's local day-key format, matching families/ids.js. */
function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ------------------------------------------------------------- pieces -- */
const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)', ...style,
  }}>
    {children}
  </section>
);

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string }> = ({ on, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={() => onChange(!on)}
    style={{
      width: 44, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 0,
      background: on ? FAM.accent : '#D6D3CC', position: 'relative', flexShrink: 0,
      transition: 'background .15s ease',
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%',
      background: '#fff', transition: 'left .15s ease', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
    }} />
  </button>
);

const TimeField: React.FC<{
  value: string; onChange: (v: string) => void; disabled: boolean; label: string;
}> = ({ value, onChange, disabled, label }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
    <span style={{
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
      color: disabled ? FAM.muted : FAM.ink3,
    }}>{label}</span>
    <input
      type="time"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        font: 'inherit', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        padding: '7px 9px', borderRadius: 10, width: '100%', minWidth: 0,
        border: `1px solid ${FAM.line}`, background: disabled ? '#F6F5F1' : '#fff',
        color: disabled ? FAM.muted : FAM.ink, cursor: disabled ? 'not-allowed' : 'text',
      }}
    />
  </label>
);

/* ================================================================ page == */
interface Props { orgCode: string }

export default function FamilyHoursPage({ orgCode }: Props) {
  const [loading, setLoading] = useState(true);
  /* `saved` is what Firestore last told us; `weekly`/`breaks` are the draft.
     Keeping both is what makes "Save changes" honest — and what lets another
     device's write land without wiping what is being typed here. */
  const [savedWeekly, setSavedWeekly] = useState<Weekly>(emptyWeekly);
  const [savedBreaks, setSavedBreaks] = useState<FamilyBreak[]>([]);
  const [weekly, setWeekly] = useState<Weekly>(emptyWeekly);
  const [breaks, setBreaks] = useState<FamilyBreak[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [breakErr, setBreakErr] = useState('');

  /* What Firestore last told us, in a ref as well as in state.
     The listener is subscribed once per orgCode, so anything it closes over
     from state is frozen at subscription time — comparing the draft against a
     stale `savedWeekly` would adopt remote values over live edits. The ref is
     always current. */
  const savedRef = useRef<{ weekly: Weekly; breaks: FamilyBreak[] }>({ weekly: emptyWeekly(), breaks: [] });

  useEffect(() => {
    if (!orgCode) { setLoading(false); return; }
    const unsub = onSnapshot(
      doc(db, 'familySchedules', orgCode),
      (snap) => {
        const d = snap.data() || {};
        const w = toWeekly(d.weekly);
        const b = toBreaks(d.breaks);
        const prev = savedRef.current;
        /* Adopt the remote value into the draft only when the draft is
           untouched relative to what we last knew was saved. Otherwise a
           serverTimestamp echo — or the parent's other device — would yank a
           half-typed time out from under them. */
        setWeekly((cur) => (sameWeekly(cur, prev.weekly) ? w : cur));
        setBreaks((cur) => (sameBreaks(cur, prev.breaks) ? b : cur));
        savedRef.current = { weekly: w, breaks: b };
        setSavedWeekly(w);
        setSavedBreaks(b);
        setLoading(false);
      },
      () => { setErr('We could not load your hours. Refresh and try again.'); setLoading(false); },
    );
    return () => unsub();
  }, [orgCode]);

  const dirty = useMemo(
    () => !sameWeekly(weekly, savedWeekly) || !sameBreaks(breaks, savedBreaks),
    [weekly, savedWeekly, breaks, savedBreaks],
  );

  const anyDayOn = useMemo(
    () => DAYS.some((d) => weekly[d.key].morning.enabled || weekly[d.key].evening.enabled),
    [weekly],
  );

  const setLeg = useCallback((dayKey: string, leg: 'morning' | 'evening', patch: Partial<Leg>) => {
    setOkMsg('');
    setWeekly((cur) => ({ ...cur, [dayKey]: { ...cur[dayKey], [leg]: { ...cur[dayKey][leg], ...patch } } }));
  }, []);

  /** Which runs the car does — both, or only the morning or the afternoon
   *  (Gabriel, 2026-10-28). Read off the days that run, so there is no
   *  separate setting to fall out of step with the week. */
  const runs = useMemo<'both' | 'morning' | 'evening'>(() => {
    const m = DAYS.some((d) => weekly[d.key].morning.enabled);
    const e = DAYS.some((d) => weekly[d.key].evening.enabled);
    if (m && !e) return 'morning';
    if (e && !m) return 'evening';
    return 'both';
  }, [weekly]);

  /** Switch the other run off (or back on) on every day that runs. */
  const chooseRuns = useCallback((next: 'both' | 'morning' | 'evening') => {
    setOkMsg('');
    setWeekly((cur) => {
      const out = { ...cur };
      for (const d of DAYS) {
        const day = cur[d.key];
        if (!day.morning.enabled && !day.evening.enabled) continue;
        out[d.key] = {
          morning: { ...day.morning, enabled: next !== 'evening' },
          evening: { ...day.evening, enabled: next !== 'morning' },
        };
      }
      return out;
    });
  }, []);

  /** The row switch drives the day's runs — a household turning Wednesday off
   *  means no school run that day. Turning it on brings back the runs the car
   *  does, not both when it only does one. */
  const setDay = useCallback((dayKey: string, on: boolean) => {
    setOkMsg('');
    setWeekly((cur) => ({
      ...cur,
      [dayKey]: {
        morning: { ...cur[dayKey].morning, enabled: on && runs !== 'evening' },
        evening: { ...cur[dayKey].evening, enabled: on && runs !== 'morning' },
      },
    }));
  }, [runs]);

  const addBreak = useCallback(() => {
    setBreakErr('');
    if (!DATE_RE.test(newFrom) || !DATE_RE.test(newTo)) {
      setBreakErr('Pick a first and last day for the break.');
      return;
    }
    if (newTo < newFrom) {
      setBreakErr('The last day cannot be before the first day.');
      return;
    }
    const label = newLabel.trim() || 'Break';
    const next = [...breaks, { from: newFrom, to: newTo, label }].sort((a, b) => a.from.localeCompare(b.from));
    setBreaks(next);
    setNewFrom(''); setNewTo(''); setNewLabel('');
    setOkMsg('');
  }, [newFrom, newTo, newLabel, breaks]);

  const save = useCallback(async () => {
    setErr(''); setOkMsg('');
    for (const d of DAYS) {
      const day = weekly[d.key];
      if (day.morning.enabled && !HHMM_RE.test(day.morning.pickupAt)) {
        setErr(`${d.label}'s morning time needs to look like 07:10.`); return;
      }
      if (day.evening.enabled && !HHMM_RE.test(day.evening.pickupAt)) {
        setErr(`${d.label}'s afternoon time needs to look like 16:00.`); return;
      }
    }
    setBusy(true);
    try {
      const days = DAYS.filter((d) => weekly[d.key].morning.enabled || weekly[d.key].evening.enabled)
        .map((d) => d.key);
      /* The first running day's times also refresh the legacy top-level
         morningAt/eveningAt written at signup. Nothing reads them today, but a
         stale pair that contradicts `weekly` is exactly the kind of drift that
         later reads as a bug. */
      const first = days[0];
      await setDoc(doc(db, 'familySchedules', orgCode), {
        orgCode,
        weekly,
        breaks,
        days,
        ...(first ? {
          morningAt: weekly[first].morning.pickupAt,
          eveningAt: weekly[first].evening.pickupAt,
        } : {}),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      savedRef.current = { weekly, breaks };
      setSavedWeekly(weekly);
      setSavedBreaks(breaks);
      setOkMsg('Saved. Your household follows these hours from now on.');
    } catch (e) {
      console.error('[families] hours save failed', e);
      const code = typeof e === 'object' && e && 'code' in e ? String((e as { code?: string }).code) : '';
      setErr(code === 'permission-denied'
        ? 'Could not save your hours. Sign out and back in, then try again. If it still fails, household saving may need a server update.'
        : 'We could not save your hours. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }, [weekly, breaks, orgCode]);

  if (loading) {
    return <Panel><p style={{ margin: 0, color: FAM.ink2, fontSize: 14.5 }}>Loading your hours…</p></Panel>;
  }

  const today = todayKey();

  return (
    <div style={{ ...FAMILY_PAGE_WRAP, paddingBottom: dirty ? 84 : 0 }}>
      {/* ── the week ───────────────────────────────────────────────────── */}
      <Panel>
        <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
          Your week
        </h2>
        <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          The days the school run happens, and when. Trasse expects the car to set off around these
          times — and asks you about it when it does not.
        </p>

        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FAM.ink2, marginBottom: 8 }}>Which runs</div>
          <div role="radiogroup" aria-label="Which runs" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {([['both', 'Both runs'], ['morning', 'Morning only'], ['evening', 'Afternoon only']] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={runs === k}
                onClick={() => { if (runs !== k) chooseRuns(k); }}
                style={{
                  height: 38, padding: '0 16px', borderRadius: 8, font: 'inherit', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
                  border: `1.5px solid ${runs === k ? FAM.accent : FAM.line}`,
                  background: runs === k ? FAM.tint : '#fff',
                  color: runs === k ? FAM.accent : FAM.ink2,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DAYS.map((d) => {
            const day = weekly[d.key];
            const on = day.morning.enabled || day.evening.enabled;
            return (
              <div
                key={d.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: runs === 'both'
                    ? 'minmax(96px,1.1fr) auto minmax(96px,1fr) minmax(96px,1fr)'
                    : 'minmax(96px,1.1fr) auto minmax(96px,2fr)',
                  gap: 12, alignItems: 'end',
                  padding: '12px 14px', borderRadius: 14,
                  border: `1px solid ${on ? FAM.tint2 : FAM.line}`,
                  background: on ? FAM.tint : '#FAF9F6',
                }}
              >
                <div style={{ alignSelf: 'center', minWidth: 0 }}>
                  <div style={{
                    fontSize: 14.5, fontWeight: 700, color: on ? FAM.ink : FAM.muted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{d.label}</div>
                  {!on && (
                    <div style={{ fontSize: 12, color: FAM.muted, marginTop: 2 }}>No runs</div>
                  )}
                </div>
                <div style={{ alignSelf: 'center' }}>
                  <Toggle on={on} onChange={(v) => setDay(d.key, v)} label={`${d.label} school run`} />
                </div>
                {runs !== 'evening' && (
                  <TimeField
                    label="Morning"
                    value={day.morning.pickupAt}
                    disabled={!day.morning.enabled}
                    onChange={(v) => setLeg(d.key, 'morning', { pickupAt: v })}
                  />
                )}
                {runs !== 'morning' && (
                  <TimeField
                    label="Afternoon"
                    value={day.evening.pickupAt}
                    disabled={!day.evening.enabled}
                    onChange={(v) => setLeg(d.key, 'evening', { pickupAt: v })}
                  />
                )}
              </div>
            );
          })}
        </div>

        <p style={{ margin: '14px 0 0', fontSize: 13, color: FAM.muted, lineHeight: 1.6 }}>
          Weekends start off. A Saturday question about a run nobody planned is the fastest way to
          teach a household to ignore Trasse — so they stay off until you turn them on.
        </p>

        {!anyDayOn && (
          <p style={{
            margin: '12px 0 0', padding: '10px 12px', borderRadius: 10,
            background: FAM.amberTint, color: FAM.amber, fontSize: 13.5, lineHeight: 1.55,
          }}>
            Every day is off. Trasse will not expect a run, or ask you about one, until you turn a day back on.
          </p>
        )}
      </Panel>

      {/* ── breaks ─────────────────────────────────────────────────────── */}
      <Panel>
        <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
          Holidays and breaks
        </h2>
        <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          Half-term, the end of term, a week away. Trasse expects nothing on these days and asks you
          nothing — your hours stay exactly as they are, ready for when you come back.
        </p>

        {breaks.length > 0 && (
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {breaks.map((b, i) => {
              const current = today >= b.from && today <= b.to;
              const past = b.to < today;
              return (
                <div
                  key={`${b.from}_${b.to}_${i}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    padding: '11px 14px', borderRadius: 12,
                    border: `1px solid ${current ? FAM.tint2 : FAM.line}`,
                    background: current ? FAM.tint : '#fff',
                    opacity: past ? 0.55 : 1,
                  }}
                >
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: FAM.ink }}>{b.label}</div>
                    <div style={{ fontSize: 13, color: FAM.ink3, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtRange(b)}
                    </div>
                  </div>
                  {current && (
                    <span style={{
                      padding: '3px 9px', borderRadius: 999, background: FAM.accent, color: '#fff',
                      fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
                    }}>ON NOW</span>
                  )}
                  {past && (
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: FAM.muted }}>PAST</span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setBreaks(breaks.filter((_, j) => j !== i)); setOkMsg(''); }}
                    style={{
                      border: `1px solid ${FAM.line}`, background: '#fff', borderRadius: 9,
                      padding: '6px 11px', font: 'inherit', fontSize: 13, fontWeight: 600,
                      color: FAM.danger, cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{
          marginTop: 18, padding: 14, borderRadius: 14,
          border: `1px dashed ${FAM.tint2}`, background: '#FAF9F6',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FAM.ink2, marginBottom: 10 }}>Add a break</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3 }}>
                What is it
              </span>
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Half-term"
                style={{
                  font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 10,
                  border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3 }}>
                First day
              </span>
              <input
                type="date" value={newFrom} onChange={(e) => setNewFrom(e.target.value)}
                style={{
                  font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 10,
                  border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3 }}>
                Last day
              </span>
              <input
                type="date" value={newTo} min={newFrom || undefined} onChange={(e) => setNewTo(e.target.value)}
                style={{
                  font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 10,
                  border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
                }}
              />
            </label>
          </div>
          {breakErr && (
            <p style={{ margin: '10px 0 0', fontSize: 13.5, color: FAM.danger }}>{breakErr}</p>
          )}
          <button
            type="button"
            onClick={addBreak}
            style={{
              marginTop: 12, padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: FAM.tint2, color: FAM.accent, font: 'inherit', fontSize: 13.5, fontWeight: 700,
            }}
          >
            Add break
          </button>
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: FAM.muted }}>
            Breaks are saved with the rest of this page — add it here, then Save changes below.
          </p>
        </div>
      </Panel>

      {err && (
        <p style={{
          margin: 0, padding: '11px 14px', borderRadius: 12,
          background: FAM.dangerTint, color: FAM.danger, fontSize: 14, lineHeight: 1.55,
        }}>{err}</p>
      )}
      {okMsg && !dirty && (
        <p style={{
          margin: 0, padding: '11px 14px', borderRadius: 12,
          background: FAM.posTint, color: FAM.pos, fontSize: 14, lineHeight: 1.55,
        }}>{okMsg}</p>
      )}

      {/* ── the save bar ───────────────────────────────────────────────────
          Only present when something changed. A permanently visible Save
          invites a parent to wonder whether they forgot to press it. */}
      {dirty && (
        <div style={{
          position: 'sticky', bottom: 16, zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '13px 18px', borderRadius: 16,
          background: '#fff', border: `1px solid ${FAM.tint2}`,
          boxShadow: '0 10px 30px rgba(76,52,148,.18)',
        }}>
          <span style={{ flex: '1 1 180px', fontSize: 14, fontWeight: 600, color: FAM.ink2 }}>
            You have unsaved changes.
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => { setWeekly(savedWeekly); setBreaks(savedBreaks); setErr(''); }}
            style={{
              padding: '9px 15px', borderRadius: 10, border: `1px solid ${FAM.line}`,
              background: '#fff', color: FAM.ink2, font: 'inherit', fontSize: 13.5, fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Discard
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={save}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: busy ? FAM.accentPale : FAM.accent,
              color: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              boxShadow: busy ? 'none' : '0 4px 13px rgba(0,112,127,.4)',
            }}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}
